import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { CONTENT } from '../content';
import { ComicReader } from '../comic/ComicReader';
import {
  Buy,
  C,
  COMIC_BOOK,
  ContractAddress,
  DrawnFooter,
  EASE_OUT,
  MEMES,
  NDHAUS_PHOTO,
  ORIGINAL_ART,
  ROADMAP,
  RedesignFonts,
  Socials,
  TITLES,
  TitleArt,
  VIDEOS,
  BuyButton,
  Lettering,
  NAV,
  UTILITIES,
  TOKEN_FACTS,
  SupplyDonut
} from '../kit';

type AvatarDef = { name: string; bg: string; color: string; pos: string; img?: string };

const AVATARS: Record<string, AvatarDef> = {
  pepe: { name: 'Pepe', bg: '#fff6e5', color: C.green, pos: '15% 70%' },
  andy: { name: 'Andy', bg: '#ffebe0', color: C.pink, pos: '85% 60%' },
  brett: { name: 'Brett', bg: '#fcf3e8', color: C.blue, pos: '40% 65%' },
  wolf: { name: 'Landwolf', bg: '#fdf0d5', color: C.orange, pos: '90% 70%' },
  zeus: { name: 'Zeus', bg: '#fffde7', color: C.yellow, pos: '60% 85%' },
  ndhaus: { name: 'ndhaus', bg: '#fcf6eb', color: C.sky, img: NDHAUS_PHOTO, pos: '50% 20%' }
};

const Lightbox = ({ src, onClose }: { src: string; onClose: () => void }) => (
  <LightboxOverlay onClick={onClose}>
    <img src={src} alt="" onClick={e => e.stopPropagation()} />
    <button onClick={onClose}>&times;</button>
  </LightboxOverlay>
);

const GalleryStrip = ({ images, onImgClick }: { images: string[], onImgClick: (src: string, index: number) => void }) => {
  return (
    <div className="gallery-strip">
      {images.map((src, i) => (
        <button key={src} type="button" onClick={() => onImgClick(src, i)}>
          <img src={src} alt="" loading="lazy" />
        </button>
      ))}
    </div>
  );
};

const Msg = ({ senderKey, children, className }: { senderKey: keyof typeof AVATARS; children: React.ReactNode; className?: string }) => {
  const [status, setStatus] = useState<'hidden' | 'typing' | 'revealed'>('hidden');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && status === 'hidden') {
        setStatus('typing');
        setTimeout(() => setStatus('revealed'), 800 + Math.random() * 600);
      }
    }, { threshold: 0, rootMargin: '0px 0px -15% 0px' });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [status]);

  const sender = AVATARS[senderKey];

  return (
    <MessageWrapper ref={ref} className={`${status !== 'hidden' ? 'visible' : ''} ${className || ''}`}>
      <div className="avatar">
        <img src={sender.img || '/redesign2/scenes/camp.webp'} alt={sender.name} style={{ objectPosition: sender.pos }} />
      </div>
      <div className="msg-content">
        <div className="sender" style={{ color: sender.color }}>{sender.name}</div>
        <div className="bubble" style={{ background: sender.bg, borderColor: sender.color }}>
          {status === 'typing' ? (
            <div className="typing"><span>.</span><span>.</span><span>.</span></div>
          ) : (
            <div className="inner fade-in">{children}</div>
          )}
        </div>
      </div>
    </MessageWrapper>
  );
};

export default function RedesignM4() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [lightboxImg, setLightboxImg] = useState<string | null>(null);
  const [comicOpen, setComicOpen] = useState(false);

  return (
    <Page id="top">
      <RedesignFonts />
      
      {menuOpen && (
        <MenuOverlay onClick={() => setMenuOpen(false)}>
          <div className="sheet" onClick={e => e.stopPropagation()}>
            <div className="handle" />
            {NAV.map(item => (
              <a key={item.label} href={item.href} onClick={() => setMenuOpen(false)}>{item.label}</a>
            ))}
            <div className="divider" />
            {UTILITIES.map(u => (
              <a key={u.label} href={u.href} onClick={() => setMenuOpen(false)}>{u.label}</a>
            ))}
          </div>
        </MenuOverlay>
      )}

      {lightboxImg && <Lightbox src={lightboxImg} onClose={() => setLightboxImg(null)} />}
      
      {comicOpen && <ComicReader pages={COMIC_BOOK} startPage={0} pdf="/zeus.pdf" onClose={() => setComicOpen(false)} />}

      <Hero>
        <div className="hero-content">
          <TitleArt src={TITLES.zeus.src} alt={TITLES.zeus.alt} $w="260px" />
          <TitleArt src={TITLES.pepesDog.src} alt={TITLES.pepesDog.alt} $w="140px" style={{ marginBottom: 20 }} />
          <ContractAddress tone="light" />
          <div style={{ marginTop: 16 }}>
            <Buy big />
          </div>
          <div style={{ marginTop: 20 }}>
            <Socials color={C.white} size={28} />
          </div>
        </div>
      </Hero>

      <ChatLog>
        <Msg senderKey="pepe">
          <p>{CONTENT.about.paragraphs[0]}</p>
          <p>{CONTENT.about.paragraphs[1]}</p>
        </Msg>

        <Msg senderKey="andy">
          <p>{CONTENT.about.paragraphs[2]}</p>
          <p>{CONTENT.about.paragraphs[3]}</p>
          <a href={CONTENT.about.trademarkLink} target="_blank" rel="noopener noreferrer" className="link">
            Check the Trademark
          </a>
        </Msg>

        <Msg senderKey="ndhaus">
          <video src={VIDEOS.trailer.src} poster={VIDEOS.trailer.poster} className="msg-video" playsInline controls autoPlay muted loop preload="metadata" />
        </Msg>

        <Msg senderKey="wolf">
          <TitleArt src={TITLES.takeOver.src} alt={TITLES.takeOver.alt} $w="220px" style={{ marginBottom: 16 }} />
          <p>{CONTENT.aboutCto.paragraphs[0]}</p>
          <video src={VIDEOS.buy.src} poster={VIDEOS.buy.poster} className="msg-video" playsInline autoPlay muted loop preload="metadata" />
        </Msg>

        <Msg senderKey="brett">
          <p>{CONTENT.aboutCto.paragraphs[1]}</p>
          <p>{CONTENT.aboutCto.paragraphs[2]}</p>
        </Msg>

        <div id="tokenomics" className="anchor" />
        <Msg senderKey="zeus">
          <TitleArt src={TITLES.tokenomics.src} alt={TITLES.tokenomics.alt} $w="240px" style={{ marginBottom: 20 }} />
          <SupplyDonut ink={C.ink} size={240} circColor={C.blue} />
        </Msg>

        <Msg senderKey="pepe">
          <div className="facts-grid">
            {TOKEN_FACTS.map(f => (
              <a key={f.title} href={f.link} target="_blank" rel="noopener noreferrer" className="fact-card">
                <img src={`/redesign2/tokenomics/${f.title === 'LP Renounced' ? 'locked.webp' : f.title === 'cONTRACT' ? 'burned.webp' : f.title === 'Tax' ? 'tax.webp' : 'supply.webp'}`} alt="" />
                <div className="fact-text">
                  <strong>{f.title}</strong>
                  <span>{f.text}</span>
                </div>
              </a>
            ))}
          </div>
          <p style={{ marginTop: 16, fontSize: 14 }}>{CONTENT.tokenomics.verifyText}</p>
        </Msg>

        <div id="dollarize" className="anchor" />
        <Msg senderKey="andy">
          <img src="/redesign2/scenes/blackhole.webp" alt="Blackhole" className="msg-img round" />
          <Lettering $size="26px" $tilt={-2} style={{ display: 'block', margin: '16px 0 12px' }}>
            {CONTENT.dollarize.title}
          </Lettering>
          {CONTENT.dollarize.paragraphs.map(p => <p key={p.slice(0, 10)}>{p}</p>)}
          <div className="button-row">
            <BuyButton href={CONTENT.dollarize.mintLink} target="_blank" rel="noopener noreferrer">
              MINT USDZEUS
            </BuyButton>
            <a href={CONTENT.dollarize.howLink} target="_blank" rel="noopener noreferrer" className="link">
              How it works
            </a>
          </div>
        </Msg>

        <div id="roadmap" className="anchor" />
        <Msg senderKey="wolf">
          <TitleArt src={TITLES.roadmap.src} alt={TITLES.roadmap.alt} $w="180px" style={{ marginBottom: 16 }} />
          <div className="roadmap-swipe">
            {ROADMAP.map(stop => (
              <div key={stop.milestone.id} className="roadmap-card">
                {stop.pill && <span className="pill">{stop.pill}</span>}
                <h4>{stop.lines.join(' ')}</h4>
                <p>{stop.milestone.description}</p>
              </div>
            ))}
          </div>
        </Msg>

        <Msg senderKey="ndhaus">
          <TitleArt src={TITLES.artist.src} alt={TITLES.artist.alt} $w="180px" style={{ marginBottom: 16 }} />
          <img src={NDHAUS_PHOTO} alt="ndhaus" className="msg-img round-avatar" />
          {CONTENT.artist.paragraphs.map(p => <p key={p.slice(0, 10)}>{p}</p>)}
          <img src={ORIGINAL_ART} alt="Original Art" className="msg-img" />
        </Msg>

        <Msg senderKey="pepe">
          <Lettering $size="24px" $tilt={2} style={{ display: 'block', marginBottom: 12 }}>
            {CONTENT.nfts.title}
          </Lettering>
          {CONTENT.nfts.paragraphs.map(p => <p key={p.slice(0,10)}>{p}</p>)}
          <a href={CONTENT.nfts.link} target="_blank" rel="noopener noreferrer" className="link">OpenSea</a>
        </Msg>

        <Msg senderKey="zeus">
          <GalleryStrip 
            images={['/redesign2/nfts/hero.webp', ...CONTENT.nfts.items.map(id => `/redesign2/nfts/${id}.webp`)]} 
            onImgClick={src => setLightboxImg(src)} 
          />
        </Msg>

        <div id="memes" className="anchor" />
        <Msg senderKey="brett">
          <TitleArt src={TITLES.memes.src} alt={TITLES.memes.alt} $w="160px" style={{ marginBottom: 12 }} />
          <GalleryStrip 
            images={MEMES.map(m => m.src)} 
            onImgClick={src => setLightboxImg(src)} 
          />
        </Msg>

        <Msg senderKey="ndhaus">
          <TitleArt src={TITLES.comic.src} alt={TITLES.comic.alt} $w="180px" style={{ marginBottom: 12 }} />
          <p>{CONTENT.comic.subtitle}</p>
          <GalleryStrip 
            images={COMIC_BOOK.slice(0, 3).map(p => p.src)} 
            onImgClick={() => setComicOpen(true)} 
          />
          <a href="/zeus.pdf" className="link" style={{ display: 'block', marginTop: 12 }}>Download PDF</a>
        </Msg>

        <Msg senderKey="zeus">
          <img src="/redesign2/scenes/gang.webp" alt="Gang" className="msg-img" />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, marginTop: 16 }}>
            <Buy big />
            <Socials color={C.ink} size={30} />
          </div>
        </Msg>
      </ChatLog>

      <QuickReplies>
        <div className="scroll-container">
          <a href="#tokenomics"><button>Tokenomics</button></a>
          <a href="#roadmap"><button>Roadmap</button></a>
          <a href="#dollarize"><button>Dollarize</button></a>
          <a href="#memes"><button>Memes</button></a>
          <button onClick={() => setMenuOpen(true)}>Menu</button>
        </div>
      </QuickReplies>
      
      <div style={{ padding: '0 16px', marginTop: 20, marginBottom: 40 }}>
        <DrawnFooter color={C.white} />
      </div>
    </Page>
  );
}

const typingAnim = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;

const Page = styled.div`
  background: #09120e;
  color: ${C.white};
  font-family: 'Ubuntu', sans-serif;
  min-height: 100dvh;
  padding-bottom: 110px;
  overflow-x: hidden;
  
  .anchor {
    scroll-margin-top: 80px;
  }
`;

const MenuOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 999;
  background: rgba(0,0,0,0.6);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  
  .sheet {
    background: ${C.white};
    border-top-left-radius: 24px;
    border-top-right-radius: 24px;
    padding: 16px 24px 40px;
    display: flex;
    flex-direction: column;
    animation: ${fadeIn} 0.2s ${EASE_OUT};
  }
  
  .handle {
    width: 40px;
    height: 4px;
    background: #ccc;
    border-radius: 2px;
    margin: 0 auto 20px;
  }
  
  a {
    font-family: 'GROBOLD', sans-serif;
    color: ${C.ink};
    text-decoration: none;
    font-size: 20px;
    padding: 12px 0;
  }
  
  .divider {
    height: 2px;
    background: #eee;
    margin: 8px 0;
  }
`;

const LightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0,0,0,0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  animation: ${fadeIn} 0.2s ${EASE_OUT};
  
  img {
    max-width: 100%;
    max-height: 90vh;
    object-fit: contain;
    border-radius: 8px;
  }
  
  button {
    position: absolute;
    top: 16px;
    right: 16px;
    background: none;
    border: none;
    color: white;
    font-size: 40px;
    line-height: 1;
    padding: 8px;
  }
`;

const Hero = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 3/4;
  min-height: 500px;
  max-height: 80vh;
  background-image: url('/redesign2/scenes/camp.webp');
  background-size: cover;
  background-position: center bottom;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 20px 16px 40px;
  
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to bottom, rgba(9,18,14,0) 60%, #09120e 95%);
  }
  
  .hero-content {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const ChatLog = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 0 16px 40px;
`;

const MessageWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 10px;
  width: 100%;
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.6s ${EASE_OUT}, transform 0.6s ${EASE_OUT};
  
  &.visible {
    opacity: 1;
    transform: none;
  }
  
  .avatar {
    width: 42px;
    height: 42px;
    flex-shrink: 0;
    border-radius: 50%;
    border: 3px solid ${C.ink};
    overflow: hidden;
    background: ${C.white};
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transform: scale(2.5);
    }
  }
  
  .msg-content {
    display: flex;
    flex-direction: column;
    max-width: calc(100% - 52px);
  }
  
  .sender {
    font-family: 'GROBOLD', sans-serif;
    font-size: 14px;
    margin-bottom: 4px;
    margin-left: 12px;
    text-shadow: 1px 1px 0 rgba(0,0,0,0.8);
  }
  
  .bubble {
    position: relative;
    color: ${C.ink};
    padding: 16px;
    border: 3px solid ${C.ink};
    border-radius: 20px 24px 24px 4px;
    font-size: 16px;
    line-height: 1.5;
    
    p { margin: 0 0 0.8em; }
    p:last-child { margin: 0; }
    
    &::before {
      content: '';
      position: absolute;
      bottom: -3px;
      left: -9px;
      width: 24px;
      height: 18px;
      background: inherit;
      border-bottom: 3px solid ${C.ink};
      border-left: 3px solid ${C.ink};
      border-bottom-left-radius: 16px;
      z-index: -1;
    }
  }
  
  .typing {
    display: flex;
    gap: 4px;
    padding: 4px 8px;
    span {
      display: block;
      width: 6px;
      height: 6px;
      background: ${C.ink};
      border-radius: 50%;
      animation: ${typingAnim} 1s infinite;
    }
    span:nth-child(2) { animation-delay: 0.2s; }
    span:nth-child(3) { animation-delay: 0.4s; }
  }
  
  .inner.fade-in {
    animation: ${fadeIn} 0.3s ${EASE_OUT};
  }
  
  .msg-video, .msg-img {
    display: block;
    width: 100%;
    height: auto;
    object-fit: contain;
    border-radius: 12px;
    border: 3px solid ${C.ink};
    margin-top: 12px;
    margin-bottom: 12px;
    background: ${C.white};
  }
  
  .msg-img.round {
    border-radius: 24px;
  }
  
  .msg-img.round-avatar {
    border-radius: 50%;
    width: 70%;
    margin: 12px auto;
  }
  
  .link {
    display: inline-block;
    font-weight: bold;
    color: ${C.ink};
    text-decoration: underline;
    text-decoration-thickness: 2px;
    text-underline-offset: 3px;
  }
  
  .button-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px;
    margin-top: 16px;
  }
  
  .facts-grid {
    display: grid;
    gap: 12px;
    margin-top: 12px;
  }
  
  .fact-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px;
    border: 2px solid ${C.ink};
    border-radius: 12px;
    text-decoration: none;
    color: inherit;
    background: ${C.white};
    img { width: 44px; height: 44px; object-fit: contain; }
    strong { display: block; font-family: 'GROBOLD', sans-serif; font-size: 15px; }
    span { font-size: 13px; opacity: 0.8; }
  }
  
  .roadmap-swipe {
    display: flex;
    gap: 12px;
    overflow-x: auto;
    padding: 4px 4px 16px 4px;
    margin: 12px -12px 0;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
  }
  
  .roadmap-card {
    flex: 0 0 85%;
    scroll-snap-align: center;
    border: 3px solid ${C.ink};
    border-radius: 16px;
    padding: 16px;
    background: ${C.white};
    h4 { margin: 0 0 8px; font-family: 'GROBOLD', sans-serif; }
    p { margin: 0; font-size: 15px; }
    .pill { 
      display: inline-block; 
      padding: 4px 8px; 
      background: ${C.yellow}; 
      border: 2px solid ${C.ink}; 
      border-radius: 99px; 
      font-size: 12px; 
      font-weight: bold; 
      margin-bottom: 8px; 
    }
  }
  
  .gallery-strip {
    display: flex;
    gap: 12px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    padding: 4px 12px 12px 12px;
    margin: 12px -12px 0;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
    button {
      flex: 0 0 auto;
      padding: 0;
      background: none;
      border: none;
      scroll-snap-align: center;
    }
    img {
      display: block;
      height: 160px;
      width: auto;
      max-width: 75vw;
      border: 3px solid ${C.ink};
      border-radius: 12px;
      object-fit: contain;
      background: ${C.white};
    }
  }
`;

const QuickReplies = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background: linear-gradient(to top, rgba(9,18,14,1) 60%, rgba(9,18,14,0));
  padding: 40px 0 calc(env(safe-area-inset-bottom, 16px) + 16px);
  z-index: 100;
  
  .scroll-container {
    display: flex;
    gap: 12px;
    overflow-x: auto;
    padding: 0 16px;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
    -webkit-mask-image: linear-gradient(to right, transparent, black 12px, black calc(100% - 12px), transparent);
    mask-image: linear-gradient(to right, transparent, black 12px, black calc(100% - 12px), transparent);
  }
  
  a { text-decoration: none; }
  
  button {
    flex: 0 0 auto;
    background: ${C.ink};
    color: ${C.white};
    border: 3px solid ${C.yellow};
    border-radius: 99px;
    padding: 10px 20px;
    font-family: 'GROBOLD', sans-serif;
    font-size: 15px;
    box-shadow: 2px 3px 0 ${C.yellow};
    transition: transform 0.1s;
    &:active {
      transform: translate(2px, 3px);
      box-shadow: none;
    }
  }
`;
